/**
 * Canonical custom lead-time tier catalog (UID-278, ADR-0085 D2/A1). The SINGLE source of
 * truth for the four tiers' ids + per-region price RATIOS, shared by the BFF pricing
 * derivation (`apps/api` `demo-pricing-catalog`) and the cart UI's display table (`apps/web`
 * `custom-region-tiers`). Framework-agnostic (no Fastify, no React — ADR-0001 §2), so both the
 * server and the browser consume ONE definition instead of two hand-synced copies.
 *
 * Only `id` + `priceRatio` live here — the data that drives money. Display concerns
 * (label / region / lead-time days / arrival-date formatting) stay in the cart UI.
 *
 * `priceRatio` is a per-manufacturing-region UNIT-COST multiplier (US/MX/IN/CN), NOT a shipping
 * premium: a sub-1.0 ratio (`in` 0.86, `cn` 0.78) cannot be a shipping charge, it only makes
 * sense as a per-region reprice of the part itself (ADR-0085 A1). The reprice is done in
 * scaled-integer money math server-side (cart-derivation) — never a bare float multiply on a
 * decimal-string amount.
 */
export interface CustomLeadTimeTier {
  readonly id: string
  readonly priceRatio: number
}

export const CUSTOM_LEAD_TIME_TIERS: readonly CustomLeadTimeTier[] = Object.freeze([
  Object.freeze({ id: 'us', priceRatio: 1.2 }),
  Object.freeze({ id: 'mx', priceRatio: 1.02 }),
  Object.freeze({ id: 'in', priceRatio: 0.86 }),
  Object.freeze({ id: 'cn', priceRatio: 0.78 }),
])

const CUSTOM_LEAD_TIME_TIER_BY_ID: ReadonlyMap<string, CustomLeadTimeTier> = new Map(
  CUSTOM_LEAD_TIME_TIERS.map((tier) => [tier.id, tier]),
)

/**
 * Whether the id names a known custom lead-time tier. The BFF rejects an unknown id with 422
 * (ADR-0085 D2) — the tier catalog is app-owned and deliberately NOT enumerated on the wire
 * contract, so this is the sole server-side gate on tier validity.
 */
export function isKnownLeadTimeTierId(id: string): boolean {
  return CUSTOM_LEAD_TIME_TIER_BY_ID.has(id)
}

/**
 * The price ratio for a known tier id, or `undefined` for an unknown one. A line with NO
 * selection reprices at the ratio-1.0 baseline (its stored price unchanged); the derivation
 * treats an unknown/absent selection as 1.0, and the API layer rejects an unknown id up front
 * with 422 so an unknown ratio never silently reaches the summary.
 */
export function leadTimeTierRatio(id: string): number | undefined {
  return CUSTOM_LEAD_TIME_TIER_BY_ID.get(id)?.priceRatio
}
